/**
 * "New folder" against the mail server itself.
 *
 * The name field holds one segment and nothing else: where the folder sits is
 * the parent picker's job, because the character that separates a path is the
 * server's choice ("/" on Zone.eu, "." on plenty of others) and a typed
 * separator would nest on one provider and become a literal name on the next.
 * The server validates the same rule; this dialog only says it up front.
 */
import { useState } from "react";
import { FolderPlus, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { createImapFolder, type ImapFolder } from "../../../../lib/mail";
import { Btn, Modal, SelectInput } from "../../../ui";
import { imapFolderError } from "./imapFolderErrors";

export function NewImapFolderModal({
  mailbox,
  folders,
  defaultParent,
  onClose,
  onCreated,
}: {
  /** The mailbox the folder is in: "shared" or an own mailbox id. */
  mailbox: string;
  folders: ImapFolder[];
  defaultParent?: string;
  onClose: () => void;
  onCreated: (path: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [parentPath, setParentPath] = useState(defaultParent ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createImapFolder({ name: name.trim(), parentPath: parentPath || undefined, mailbox });
      onCreated(res.path);
      onClose();
    } catch (err) {
      setError(imapFolderError(t, err, "email.imap.createFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("email.imap.newTitle")}
      label={t("email.imap.newTitle")}
      onClose={onClose}
      className="max-w-[440px]"
    >
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div>
          <span className="crm-lab">{t("email.imap.nameField")} *</span>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("email.imap.namePlaceholder")}
              autoComplete="off"
              autoFocus
              maxLength={64}
              required
            />
          </label>
          <span className="crm-sub !mt-1.5 block text-[11.5px] leading-snug">{t("email.imap.nameHint")}</span>
        </div>

        <div>
          <span className="crm-lab">{t("email.imap.parentField")}</span>
          <SelectInput value={parentPath} onChange={setParentPath} label={t("email.imap.parentField")}>
            <option value="">{t("email.imap.parentTop")}</option>
            {/* Only a selectable folder can hold children; a Noselect node is
                a path segment the server will not accept as a parent. */}
            {folders
              .filter((f) => f.selectable)
              .map((f) => (
                <option key={f.path} value={f.path}>
                  {`${"  ".repeat(f.depth)}${f.name}`}
                </option>
              ))}
          </SelectInput>
        </div>

        {error ? <p className="text-[12.5px] leading-snug text-destructive">{error}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <FolderPlus className="size-3.5" />}
            {t("email.imap.create")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
